import React from 'react';
import { Clock, Calendar } from 'lucide-react';
import { Event } from '../../../types';

interface EventCardProps {
  event: Event;
  language: 'en' | 'ar';
  strings: any;
  getEventTypeName: (typeId: string) => string;
  getPlaceTypeName: (typeId: string) => string;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  language,
  strings,
  getEventTypeName,
  getPlaceTypeName
}) => {
  const parseEventDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    try {
      // Check for DD-MM-YYYY format (European format used in your database)
      if (dateString.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);   // First part is day
          const month = parseInt(parts[1]); // Second part is month
          const year = parseInt(parts[2]);  // Third part is year
          
          console.log('🔧 Parsing DD-MM-YYYY in EventCard:', {
            original: dateString,
            day: day,
            month: month,
            year: year,
            parsedDate: new Date(year, month - 1, day).toISOString()
          });
          
          return new Date(year, month - 1, day); // Subtract 1 only when creating Date object
        }
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in YYYY-MM-DD format
        return new Date(dateString);
      } else {
        // Try direct parsing for other formats
        const parsedDate = new Date(dateString);
        return !isNaN(parsedDate.getTime()) ? parsedDate : null;
      }
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
      return null;
    }
    
    return null;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return language === 'ar' ? 'التاريخ غير محدد' : 'Date not specified';
    
    const parsedDate = parseEventDate(dateString);
    if (!parsedDate) return language === 'ar' ? 'تاريخ غير صحيح' : 'Invalid Date';
    
    return parsedDate.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString || timeString === 'الوقت غير محدد' || timeString === 'Time not specified') {
      return language === 'ar' ? 'الوقت غير محدد' : 'Time not specified';
    }
    return timeString;
  };

  const getStatusColor = (status: 'scheduled' | 'completed' | 'cancelled' | 'postponed' | number) => {
    if (typeof status === 'string') {
      switch (status) {
        case 'scheduled':
          return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
        case 'completed':
          return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
        case 'cancelled':
          return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
        case 'postponed':
          return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      }
    } else {
      switch (status) {
        case 1: // Upcoming
          return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
        case 2: // Completed
          return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
        case 3: // Cancelled
          return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      }
    }
  };

  const getStatusText = (status: 'scheduled' | 'completed' | 'cancelled' | 'postponed' | number) => {
    if (typeof status === 'string') {
      switch (status) {
        case 'scheduled':
          return language === 'ar' ? 'مجدول' : 'Scheduled';
        case 'completed':
          return language === 'ar' ? 'مكتمل' : 'Completed';
        case 'cancelled':
          return language === 'ar' ? 'ملغي' : 'Cancelled';
        case 'postponed':
          return language === 'ar' ? 'مؤجل' : 'Postponed';
        default:
          return language === 'ar' ? 'غير محدد' : 'Unknown';
      }
    } else {
      switch (status) {
        case 1:
          return language === 'ar' ? 'قادم' : 'Upcoming';
        case 2:
          return language === 'ar' ? 'مكتمل' : 'Completed';
        case 3:
          return language === 'ar' ? 'ملغي' : 'Cancelled';
        default:
          return language === 'ar' ? 'غير محدد' : 'Unknown';
      }
    }
  };

  return (
    <div className="p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md border-slate-200 dark:border-slate-700 hover:border-primary/30">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 dark:text-white mb-1 line-clamp-1">
            {event.legalFileTitle || (language === 'ar' ? 'عنوان غير محدد' : 'Untitled Event')}
          </h3>
          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(event.date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(event.time)}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
            {getStatusText(event.status)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
